import { useMutation, useQuery } from "@tanstack/react-query";

import {
  getExpenseDescriptions,
  getExpenseSuppliers,
  getExpenseTypes,
  getExpenseUnits,
  addExpenseDescription,
  addExpenseSupplier,
  Payload,
  createExpensesBulk,
  reactivateExpenseDescription,
  reactivateExpenseSupplier,
} from "../api/expenses";
import { getExpenseHistories } from "../api/expenses";
import { AxiosRequestConfig } from "axios";
import { ExpenseType } from "@/types/expenseType";
import { ExpenseDescription } from "@/types/expenseDescription";
import { ExpenseUnit } from "@/types/expenseUnit";
import { Supplier } from "@/types/supplier";
import { Expense } from "@/types/expense";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { queryClient } from "@/api/queryClient";

interface UseGetExpensesProps {
  config: AxiosRequestConfig;
  staleTime?: number;
  gcTime?: number;
  enabled?: boolean;
}

interface UseExpenseMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseTypes = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<ExpenseType[]>, Error, ExpenseType[]>({
    queryKey: ["expense-types", config.params],
    queryFn: () => getExpenseTypes(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseDescriptions = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<
    ApiResponse<ExpenseDescription[]>,
    Error,
    ExpenseDescription[]
  >({
    queryKey: ["expense-descriptions", config.params],
    queryFn: () => getExpenseDescriptions(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseUnits = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<ExpenseUnit[]>, Error, ExpenseUnit[]>({
    queryKey: ["expense-units", config.params],
    queryFn: () => getExpenseUnits(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseSuppliers = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<Supplier[]>, Error, Supplier[]>({
    queryKey: ["expense-suppliers", config.params],
    queryFn: () => getExpenseSuppliers(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseHistories = ({
  config,
  staleTime = 1000 * 60 * 5,
  gcTime = 1000 * 60 * 5,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<Expense[]>, Error, Expense[]>({
    queryKey: ["expense-histories", config.params],
    queryFn: () => getExpenseHistories(config),
    select: (response) => response.data,
    staleTime,
    gcTime,
    enabled,
  });
};

export const useGetExpenseHistoriesPagination = ({
  config,
  staleTime = 1000 * 60 * 5,
  gcTime = 1000 * 60 * 5,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<
    PaginatedApiResponse<Expense[]>,
    Error,
    { data: Expense[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-histories-pagination", config.params],
    queryFn: () => getExpenseHistories(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
    staleTime,
    gcTime,
    enabled,
  });
};

export const useGetExpenseHistoriesOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<Expense[]>, Error, Expense[]>({
    queryKey: ["expense-histories-options", config?.params],
    queryFn: () => getExpenseHistories(config),
    select: (response) => response.data,
  });
};

export const useAddExpenseDescription = (
  options?: UseExpenseMutationOptions<ExpenseDescription>
) => {
  return useMutation<ExpenseDescription, Error, Payload>({
    mutationFn: addExpenseDescription,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useReactivateExpenseDescription = (
  options?: UseExpenseMutationOptions<ExpenseDescription>
) => {
  return useMutation<ExpenseDescription, Error, string>({
    mutationFn: reactivateExpenseDescription,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useAddExpenseSupplier = (
  options?: UseExpenseMutationOptions<Supplier>
) => {
  return useMutation<Supplier, Error, Payload>({
    mutationFn: addExpenseSupplier,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useReactivateExpenseSupplier = (
  options?: UseExpenseMutationOptions<Supplier>
) => {
  return useMutation<Supplier, Error, string>({
    mutationFn: reactivateExpenseSupplier,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useCreateExpensesBulk = (
  options?: UseExpenseMutationOptions<Expense[]>
) => {
  return useMutation<Expense[], Error, {}>({
    mutationFn: createExpensesBulk,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["expense-histories-pagination"],
      });
      queryClient.invalidateQueries({ queryKey: ["expense-histories"] });
      queryClient.invalidateQueries({ queryKey: ["expense-drafts"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};
